import { Injectable } from '@angular/core'
import { isNullOrUndefined } from '@gofive/angular-common'
import { Observable, map } from 'rxjs'
import { ClientTypeModel, PaymentTermsTypeModel } from 'src/domain/models/client.model'
import { DataTableModel } from 'src/domain/models/datatable.model'
import { ClientRepository } from 'src/domain/repositories/client.repository'
import {
	ActivityTypeEntity,
	DefaultResultData,
	DefaultResultDataSource,
	GetFilterWithCountTypeEntity,
} from '../entities/base.entity'
import {
	AddressTypeEntity,
	ClientDataTableTypeEntity,
	ClientTypeEntity,
	CustomerTypeEntity,
} from '../entities/client.entity'
import { ActivityMapper } from '../mappers/base/activity.mapper'
import { GetFilterWithCountMapper } from '../mappers/base/get-filter-with-count.mapper'
import { AddressMapper } from '../mappers/client/address.mapper'
import { ClientDataTableMapper } from '../mappers/client/client-data-table.mapper'
import { ClientMapper } from '../mappers/client/client.mapper'
import { CustomerTypeMapper } from '../mappers/client/customer-type.mapper'
import { PaymentTermsMapper } from '../mappers/client/payment-terms.mapper'

@Injectable({
	providedIn: 'root',
})
export class ClientImplementationRepository extends ClientRepository {
	clientDataTableMapper = new ClientDataTableMapper()
	getFilterWithCountMapper = new GetFilterWithCountMapper()
	clientMapper = new ClientMapper()
	customerTypeMapper = new CustomerTypeMapper()
	activityMapper = new ActivityMapper()
	paymentTermsMapper = new PaymentTermsMapper()
	addressMapper = new AddressMapper()

	GetUser() {
		return this.get<any>('api/Clients/MyCompany')
	}

	GetClientAsync(params: { id: number }) {
		return this.get<DefaultResultData<ClientTypeEntity>>('api/Clients/' + params.id).pipe(
			map((response) => {
				return this.clientMapper.mapFrom(response.data)
			}),
		)
	}

	GetClients() {
		return this.get<DefaultResultData<ClientTypeEntity[]>>('api/Clients').pipe(
			map((response) => {
				return response.data.map((item) => {
					return this.clientMapper.mapFrom(item)
				})
			}),
		)
	}

	GetTransaction(params: { id: number }) {
		return this.get<DefaultResultData<ActivityTypeEntity[]>>('api/Activities/' + params.id + '/Clients').pipe(
			map((response) => {
				return response.data.map((item) => {
					return this.activityMapper.mapFrom(item)
				})
			}),
		)
	}

	GetPaymentTerms() {
		return this.get<DefaultResultData<PaymentTermsTypeModel[]>>('api/masterTypes/paymentTerms').pipe(
			map((response) => {
				return response.data.map((item) => {
					return this.paymentTermsMapper.mapFrom(item)
				})
			}),
		)
	}

	GetCustomerType() {
		return this.get<DefaultResultData<CustomerTypeEntity[]>>('api/MasterTypes/CustomerType').pipe(
			map((response) => {
				return response.data.map((item) => {
					return this.customerTypeMapper.mapFrom(item)
				})
			}),
		)
	}

	AddClient(params: { model: ClientTypeModel }) {
		return this.post<any>('api/Clients', params.model)
	}

	EditClient(params: { model: ClientTypeModel }) {
		return this.put<any>('api/Clients/' + params.model.clientId, params.model)
	}

	GetAllAddress(params: { id: number }) {
		return this.get<DefaultResultData<AddressTypeEntity[]>>('api/Addresses/' + params.id + '/All').pipe(
			map((response) => {
				return response.data.map((item) => {
					return this.addressMapper.mapFrom(item)
				})
			}),
		)
	}

	GetDataTableClient(params: {
		model: DataTableModel
		preCondition: Object
		param?: Array<string>
		search?: string
		filter?: Object
	}): Observable<DefaultResultDataSource<ClientDataTableTypeEntity[]>[]> {
		let whereCause: string
		if (params.filter && Object.values(params.filter).filter((x) => !isNullOrUndefined(x)).length > 0) {
			whereCause = this.createWhereCause(params.preCondition, params.param, params.search, params.filter)
		} else if (!isNullOrUndefined(params.search)) {
			whereCause = this.createWhereCause(params.preCondition, params.param, params.search)
		} else {
			whereCause = this.createWhereCause(params.preCondition)
		}
		params.model.whereCause = whereCause
		if (params.model.orderBy === null) {
			params.model.orderBy = 'ClientId'
		}

		return this.post<DefaultResultDataSource<ClientDataTableTypeEntity[]>[]>('api/Clients/Search', params.model).pipe(
			map((response) => {
				return response.map((item) => {
					return {
						dataList: item.dataList,
						dataSource:
							item.dataSource?.map((i) => {
								return this.clientDataTableMapper.mapFrom(i)
							}) ?? [],
						count: item.count,
					}
				}) as DefaultResultDataSource<ClientDataTableTypeEntity[]>[]
			}),
		)
	}

	CreateUserAccountVenio(params: { model: Object }) {
		if (params.model) {
			return this.post<any>('api/Clients/CreateUserAccountVenio', params.model)
		}
	}

	CreateUserAccountEmpeo(params: { model: Object }) {
		if (params.model) {
			return this.post<any>('api/Clients/CreateUserAccountEmpeo', params.model)
		}
	}

	ChangePassword(params: { id: number; password: string }) {
		if (params.id && params.password) {
			let model = {
				clientId: params.id,
				password: params.password,
			}
			return this.post<any>(`api/Clients/ChangePassword`, model)
		}
	}

	GetFilterWithCount(params: {
		model: DataTableModel
		preCondition: Object
		param?: Array<string>
		search?: string
		filter?: Object
	}) {
		let whereCause: string
		if (params.filter && Object.values(params.filter).filter((x) => !isNullOrUndefined(x)).length > 0) {
			whereCause = this.createWhereCause(params.preCondition, params.param, params.search, params.filter)
		} else if (!isNullOrUndefined(params.search)) {
			whereCause = this.createWhereCause(params.preCondition, params.param, params.search)
		} else {
			whereCause = this.createWhereCause(params.preCondition)
		}
		params.model.whereCause = whereCause
		if (params.model.orderBy === null) {
			params.model.orderBy = 'ClientId'
		}
		return this.post<DefaultResultData<GetFilterWithCountTypeEntity[]>>(
			'api/Clients/GetFilterWithCount',
			params.model,
		).pipe(
			map((response) => {
				return response.data.map((item) => {
					return this.getFilterWithCountMapper.mapFrom(item)
				})
			}),
		)
	}

	GetClientIdByCustomerCode(params: { code: number }) {
		return this.get<any>('api/Clients/by-customer-code/' + params.code)
	}

	GetClientDetailsByCustomerCode(params: { code: number; taxId: string }) {
		return this.get<any>(`api/Clients/detail?customerCode=${params.code}&taxId=${params.taxId}`)
	}
}
